import { useConnectionState } from '../lib/hooks/use-connection-state';
import { useRetryStore } from '../stores/retry-store';

interface BannerAction {
	label: string;
	onClick: () => void;
}

interface BannerConfig {
	text: string;
	color: string;
	action: BannerAction | null;
}

const WARNING_COLOR = 'var(--khal-status-warning)';
const ERROR_COLOR = 'var(--khal-status-error)';

/**
 * Persistent banner shown below the TopBar when the NATS connection
 * is in a degraded state (reconnecting, auth expired, version mismatch)
 * OR when the WS retry wrapper is actively retrying (Tauri desktop mode).
 */
export function ConnectionBanner() {
	const { state, detail } = useConnectionState();
	const retry = useRetryStore();

	// --- Retry-based banner (Tauri desktop mode) ---
	// If the retry wrapper is active, it takes priority over NatsClient state
	if (retry.active) {
		let retryConfig: BannerConfig | null = null;

		switch (retry.status) {
			case 'connecting':
				retryConfig = {
					text: 'Connecting to server\u2026',
					color: WARNING_COLOR,
					action: null,
				};
				break;
			case 'retry_waiting':
				retryConfig = {
					text: `Reconnecting in ${retry.retryIn}s\u2026 (attempt ${retry.retryCount}/${retry.maxRetries})`,
					color: WARNING_COLOR,
					action: retry.onManualRetry
						? { label: 'Retry now', onClick: () => retry.onManualRetry?.() }
						: null,
				};
				break;
			case 'retry_failed':
				retryConfig = {
					text: 'Connection failed',
					color: ERROR_COLOR,
					action: retry.onManualRetry
						? { label: 'Retry', onClick: () => retry.onManualRetry?.() }
						: null,
				};
				break;
			default:
				break;
		}

		if (retryConfig) {
			return <BannerBar config={retryConfig} />;
		}
	}

	// --- NatsClient state-based banner (web + desktop fallback) ---
	// Don't show banner when connected or in initial disconnected state
	if (state === 'connected' || state === 'disconnected') return null;

	const configs: Record<string, BannerConfig> = {
		reconnecting: {
			text: 'Reconnecting to server\u2026',
			color: WARNING_COLOR,
			action: null,
		},
		auth_expired: {
			text: 'Session expired.',
			color: ERROR_COLOR,
			action: {
				label: 'Sign in again',
				onClick: () => {
					window.location.href = '/';
				},
			},
		},
		version_mismatch: {
			text: `Update required.${detail?.required ? ` Server requires v${detail.required}.` : ''}`,
			color: WARNING_COLOR,
			action:
				detail?.download && typeof detail.download === 'string'
					? {
							label: 'Download',
							onClick: () => window.open(detail.download as string, '_blank'),
						}
					: null,
		},
	};

	const config = configs[state];
	if (!config) return null;

	return <BannerBar config={config} />;
}

/** Shared banner UI — extracted to avoid duplication. */
function BannerBar({ config }: { config: BannerConfig }) {
	return (
		<div
			style={{
				position: 'fixed',
				top: 36,
				left: 0,
				right: 0,
				zIndex: 9400,
				height: 32,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				gap: 12,
				background: 'var(--khal-surface)',
				backdropFilter: 'blur(20px) saturate(1.8)',
				WebkitBackdropFilter: 'blur(20px) saturate(1.8)',
				borderLeft: `4px solid ${config.color}`,
				borderBottom: '1px solid var(--khal-taskbar-border)',
				fontSize: 13,
				color: 'var(--khal-text-primary)',
			}}
		>
			<span>{config.text}</span>
			{config.action && (
				<button
					type="button"
					onClick={config.action.onClick}
					style={{
						fontSize: 12,
						fontWeight: 500,
						color: config.color,
						background: `color-mix(in srgb, ${config.color} 12%, transparent)`,
						border: `1px solid color-mix(in srgb, ${config.color} 30%, transparent)`,
						borderRadius: 9999,
						padding: '2px 10px',
						cursor: 'default',
						lineHeight: '18px',
					}}
				>
					{config.action.label}
				</button>
			)}
		</div>
	);
}
